/**
 * Per-habit check-in response option editor.
 *
 * A dialog over the four /api/check-ins option endpoints (migration 0028):
 * GET lazily seeds a habit's default catalogue server-side, PUT is a partial
 * edit with exclude-unset semantics (only changed fields are sent; an
 * explicitly-null score_value clears the score), POST authors a custom
 * option, and DELETE is a soft delete — options are deactivated, never
 * removed, because recorded events reference them. The UI mirrors those
 * rules exactly rather than inventing its own.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';

import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** The seven user-pickable response categories. DEFERRED stays queue-machinery
 * only; UNSURE is always presented as "Don't recall" in the UI. Mirrors
 * ScorePresetsSection. */
const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'partial', label: 'Partial' },
  { value: 'missed', label: 'Missed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'snoozed', label: 'Snoozed' },
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'unsure', label: "Don't recall" },
];

const categoryDisplay = (value: string): string =>
  CATEGORY_OPTIONS.find((c) => c.value === value)?.label ?? value;

interface CheckInOption {
  id: number;
  habit_id: number;
  response_category: string;
  label: string;
  score_value: number | null;
  sort_order: number;
  is_active: boolean;
  show_in_queue: boolean;
  is_user_defined: boolean;
}

/** One listed option plus its editable drafts. The score is kept as raw text
 * so a cleared field stays distinguishable from 0 — blank commits as an
 * explicit null (scoreless). */
interface OptionRow {
  option: CheckInOption;
  labelDraft: string;
  scoreDraft: string;
}

const rowFromOption = (option: CheckInOption): OptionRow => ({
  option,
  labelDraft: option.label,
  scoreDraft: option.score_value === null ? '' : String(option.score_value),
});

const scoreInvalid = (raw: string): boolean => {
  if (raw.trim() === '') return false;
  const value = Number(raw);
  return Number.isNaN(value) || value < 0 || value > 10;
};

interface Props {
  habitId: number;
  habitTitle: string;
  open: boolean;
  onClose: () => void;
}

const CheckInOptionsEditor: React.FC<Props> = ({
  habitId,
  habitTitle,
  open,
  onClose,
}) => {
  const [rows, setRows] = useState<OptionRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [addCategory, setAddCategory] = useState('completed');
  const [addLabel, setAddLabel] = useState('');
  const [addScore, setAddScore] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchOptions = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/check-ins/habits/${habitId}/options`,
      );
      // Crash-proof: a proxy hiccup serving a non-array body with a 200 must
      // degrade to the empty state, not take down the dialog on rows.map.
      const options: CheckInOption[] = Array.isArray(res.data) ? res.data : [];
      const sorted = [...options].sort(
        (a, b) => a.sort_order - b.sort_order || a.id - b.id,
      );
      setRows(sorted.map(rowFromOption));
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load check-in options'));
    }
  }, [habitId]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setAddCategory('completed');
    setAddLabel('');
    setAddScore('');
    fetchOptions();
  }, [open, fetchOptions]);

  const updateRow = (index: number, patch: Partial<OptionRow>) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  // Every mutation is a minimal payload: PUT applies exclude-unset partial
  // semantics server-side, so absent fields stay untouched.
  const putOption = async (optionId: number, payload: Record<string, unknown>) => {
    try {
      await axios.put(`${API_BASE_URL}/api/check-ins/options/${optionId}`, payload);
      setError(null);
      await fetchOptions();
    } catch (err: any) {
      // A rename onto an existing (category, label) pair lands here (409) —
      // the draft is kept on screen for a correction.
      setError(getApiErrorMessage(err, 'Failed to update the check-in option'));
    }
  };

  const commitLabel = (index: number) => {
    const row = rows[index];
    const trimmed = row.labelDraft.trim();
    if (trimmed === '') {
      // An empty label is invalid (1-120 chars) — restore the saved one.
      updateRow(index, { labelDraft: row.option.label });
      return;
    }
    if (trimmed === row.option.label) return;
    putOption(row.option.id, { label: trimmed });
  };

  const commitScore = (index: number) => {
    const row = rows[index];
    const raw = row.scoreDraft.trim();
    if (scoreInvalid(raw)) {
      setError('Scores must be between 0 and 10');
      return;
    }
    if (raw === '') {
      if (row.option.score_value === null) return;
      // Blank means scoreless: the Update schema clears on an explicit null.
      putOption(row.option.id, { score_value: null });
      return;
    }
    const value = Number(raw);
    if (value === row.option.score_value) return;
    putOption(row.option.id, { score_value: value });
  };

  const deactivateOption = async (optionId: number) => {
    try {
      // Soft delete: events reference options, so the backend deactivates
      // the row rather than deleting it. The Active switch reactivates.
      await axios.delete(`${API_BASE_URL}/api/check-ins/options/${optionId}`);
      setError(null);
      await fetchOptions();
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to deactivate the check-in option'));
    }
  };

  const addOption = async () => {
    setAdding(true);
    try {
      await axios.post(`${API_BASE_URL}/api/check-ins/habits/${habitId}/options`, {
        response_category: addCategory,
        label: addLabel.trim(),
        score_value: addScore.trim() === '' ? null : Number(addScore),
      });
      setError(null);
      setAddCategory('completed');
      setAddLabel('');
      setAddScore('');
      await fetchOptions();
    } catch (err: any) {
      // A (category, label) duplicate in any state is a 409 — the backend
      // says to reactivate the existing row instead.
      setError(getApiErrorMessage(err, 'Failed to add the check-in option'));
    } finally {
      setAdding(false);
    }
  };

  const addDisabled =
    adding || addLabel.trim() === '' || scoreInvalid(addScore);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{`Customize responses — ${habitTitle}`}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Renames never rewrite history: past answers keep the label and score
          they were answered with. Applying a score preset later replaces these
          labels and scores. Leave the score blank for an answer with no score.
        </Typography>

        <Stack spacing={1.5}>
          {rows.map((row, idx) => (
            <Stack
              key={row.option.id}
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={row.option.is_active ? undefined : { opacity: 0.6 }}
            >
              <Typography variant="body2" sx={{ width: 110, flexShrink: 0 }}>
                {categoryDisplay(row.option.response_category)}
              </Typography>
              <TextField
                size="small"
                label="Label"
                value={row.labelDraft}
                onChange={(e) => updateRow(idx, { labelDraft: e.target.value })}
                onBlur={() => commitLabel(idx)}
                inputProps={{
                  maxLength: 120,
                  'aria-label': `Label for ${row.option.label}`,
                }}
                sx={{ flexGrow: 1, minWidth: 160 }}
              />
              <TextField
                size="small"
                type="number"
                label="Score"
                value={row.scoreDraft}
                onChange={(e) => updateRow(idx, { scoreDraft: e.target.value })}
                onBlur={() => commitScore(idx)}
                inputProps={{
                  min: 0,
                  max: 10,
                  step: 0.1,
                  'aria-label': `Score for ${row.option.label}`,
                }}
                sx={{ width: 100 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={row.option.show_in_queue}
                    onChange={(e) =>
                      putOption(row.option.id, { show_in_queue: e.target.checked })
                    }
                    inputProps={{
                      'aria-label': `Show in queue for ${row.option.label}`,
                    }}
                  />
                }
                label="In queue"
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={row.option.is_active}
                    onChange={(e) =>
                      putOption(row.option.id, { is_active: e.target.checked })
                    }
                    inputProps={{
                      'aria-label': `Active for ${row.option.label}`,
                    }}
                  />
                }
                label="Active"
              />
              {row.option.is_user_defined && (
                <Chip size="small" variant="outlined" label="Custom" />
              )}
              {row.option.is_active && (
                <Tooltip title="Options are never deleted — past answers reference them. This deactivates the option; the Active switch brings it back.">
                  <IconButton
                    size="small"
                    aria-label={`Deactivate ${row.option.label}`}
                    onClick={() => deactivateOption(row.option.id)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          ))}
        </Stack>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Add option
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ width: 160 }}>
              <InputLabel id="check-in-option-add-category">Category</InputLabel>
              <Select
                labelId="check-in-option-add-category"
                label="Category"
                value={addCategory}
                onChange={(e) => setAddCategory(String(e.target.value))}
              >
                {CATEGORY_OPTIONS.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Label"
              value={addLabel}
              onChange={(e) => setAddLabel(e.target.value)}
              inputProps={{ maxLength: 120, 'aria-label': 'New option label' }}
              sx={{ flexGrow: 1, minWidth: 160 }}
            />
            <TextField
              size="small"
              type="number"
              label="Score"
              value={addScore}
              onChange={(e) => setAddScore(e.target.value)}
              inputProps={{
                min: 0,
                max: 10,
                step: 0.1,
                'aria-label': 'New option score',
              }}
              sx={{ width: 100 }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addOption}
              disabled={addDisabled}
            >
              Add option
            </Button>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInOptionsEditor;
